import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { PermissionContext } from "../../contexts/PermissionContext";
import { IContractVariationActionsFormSection } from "../../interfaces";
import { Role } from "../../utils";
import { DeleteDraftDialog } from "../Shared/DeleteDraftDialog";
import { FormSectionHeader } from "../Shared/FormSectionHeader";
import { UnknownStatus } from "../Shared/UnknownStatus";

const EndContractActions = (
    endCallback: ((consumeReason: string, newContract: boolean) => void) | undefined,
    consumed: string[],
    reason: string,
    setReason: (reason: string) => void,
    newContract: boolean,
    setNewContract: (newContract: boolean) => void
) => {

    const canEnd = () => {

        //the variation details form is invalid
        if (!endCallback)
            return false;

        //invalid reason for consumed material
        if (consumed.length > 0 && reason.length === 0)
            return false;

        return true;
    }

    return (
        <Card>

            <FormSectionHeader label="End Contract" />

            <CardContent>

                <Typography variant="subtitle1">
                    Once a reason for variation is entered you can end the contract. Ending the contract will return materials to the inventory.
                    Alternately the materials can be moved to a new contract if they are being allocated to another supplier.
                </Typography>

                <Typography variant="subtitle1">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newContract}
                                onChange={(e) => setNewContract(e.target.checked)}
                            />
                        }
                        label={"Create a new contract for these materials."}
                    />
                </Typography>






                {consumed.length > 0 && <Box sx={{ my: 2 }}>

                    <Typography variant="subtitle1">
                        Consumed materials will not be returned to the inventory at the end of the contract and they will be excluded from
                        the new contract if materials are being assigned to another supplier.

                        To consume the materials selected <strong>({consumed.length})</strong>, please provide a reason.
                    </Typography>
                    <Grid container sx={{ my: 3 }}>
                        <Grid md={6} xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                required
                                label="Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                }

                <Button
                    variant="contained"
                    color="error"
                    onClick={() => endCallback ? endCallback(reason, newContract) : {}}
                    disabled={!canEnd()}>
                    End Contract
                </Button>

            </CardContent>
        </Card>
    );
}

const SubmitVariationActions = (
    deleteCallback: () => void,
    submitCallback: ((consumeReason: string) => void) | undefined,
    consumed: string[],
    reason: string,
    setReason: (reason: string) => void
) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);

    const onDelete = (confirm: boolean) => {

        if (confirm) {
            setShowDeletePrompt(false);
            setLoading(true);
            deleteCallback();

        } else setShowDeletePrompt(false);
    }

    const canSubmit = () => {

        //the variation details form is invalid
        if (!submitCallback)
            return false;

        //invalid reason for consumed material
        if (consumed.length > 0 && reason.length === 0)
            return false;

        return true;
    }

    return (
        <Card>

            <FormSectionHeader label="Submit Request" />

            <CardContent>

                <Typography variant="subtitle1">
                    When all changes have been made, please submit the contract variation for approval.

                    Removed materials will be added to a Storage Request when this variation is submitted.
                </Typography>

                {consumed.length > 0 && <Box sx={{ my: 2 }}>

                    <Typography variant="subtitle1">
                        Consumed materials will not be added to the storage request.
                        To consume the materials selected <strong>({consumed.length})</strong>, please provide a reason.
                    </Typography>
                    <Grid container sx={{ my: 3 }}>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                required
                                label="Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                }

                <Typography variant="subtitle1">
                    Before submitting, you have the option to delete this draft; you can not delete a request once it has been submitted.
                </Typography>

                <Button
                    variant="contained"

                    onClick={() => submitCallback ? submitCallback(reason) : {}}
                    disabled={!canSubmit()}>
                    Submit
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowDeletePrompt(true)}
                    disabled={loading}>Delete</Button>
            </CardContent>

            <DeleteDraftDialog open={showDeletePrompt} handleClose={onDelete} />

        </Card>
    );
}

const ApprovalActions = (
    allowed: boolean,
    approveCallback: () => void,
    rejectCallback: (r: string) => void,
) => {

    const [reasonPrompt, setReasonPrompt] = useState(false);
    const [reason, setReason] = useState("");


    return (
        <Card>

            <FormSectionHeader label="Request Approval" />

            {allowed ?
                <CardContent>


                    <Typography variant="subtitle1">
                        Please assess the materials in this contract / lease list variation request and either <strong>Approve</strong> or <strong>Reject</strong> the request.
                    </Typography>

                    <Button variant="contained" onClick={approveCallback} disabled={reasonPrompt}>
                        Approve
                    </Button>

                    <Button variant="outlined" onClick={() => setReasonPrompt(true)} disabled={reasonPrompt}>
                        Reject
                    </Button>

                    {reasonPrompt &&

                        <Box sx={{ my: 2 }}>

                            <Typography variant="subtitle1">
                                To <strong>Reject</strong> this request please provide a reason
                            </Typography>
                            <Grid container sx={{ my: 3 }}>
                                <Grid md={6} xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        required
                                        label="Reason"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <div>
                                <Button
                                    variant="contained"
                                    color="warning" onClick={() => rejectCallback(reason)}
                                    disabled={reason.length === 0}
                                >
                                    Confirm Rejection
                                </Button>
                                <Button variant="outlined" onClick={() => setReasonPrompt(false)}>
                                    Cancel
                                </Button>
                            </div>
                        </Box>

                    }

                </CardContent>

                :

                <CardContent>

                    <Typography variant="subtitle1" color="GrayText">
                        This storage request is awaiting approval from Asset Protection. There are currently no further actions available.
                    </Typography>

                </CardContent>
            }

        </Card>
    );
}

const ApprovedActions = () => {

    return (
        <Card>
            <FormSectionHeader label="Approved" />

            <CardContent>
                <Grid container >
                    <Grid xs={12} item>
                        <Typography variant="subtitle1">
                            This contract / lease spare list variation has been approved.
                            If a storage request was created for removed material it will be linked at the top of this variation.
                            <br />There are no further actions available.
                        </Typography>
                    </Grid>
                </Grid>

            </CardContent>

        </Card >
    );
}

const RejectedActions = (
    submitCallback: ((consumeReason: string) => void) | undefined,
    closeCallback: () => void,
    consumed: string[],
    reason: string,
    setReason: (reason: string) => void
) => {


    const canSubmit = () => {

        //the variation details form is invalid
        if (!submitCallback)
            return false;

        //invalid reason for consumed material
        if (consumed.length > 0 && reason.length === 0)
            return false;

        return true;
    }


    return (
        <Card>

            <FormSectionHeader label="Close or Resubmit Request" />

            <CardContent>

                <Typography variant="subtitle1">
                    The contract / lease spare list variation was rejected. You can close this request or amend it and resubmit for approval.
                </Typography>

                {consumed.length > 0 && <Box sx={{ my: 2 }}>

                    <Typography variant="subtitle1">
                        Consumed materials will not be added to the storage request.
                        To consume the materials selected <strong>({consumed.length})</strong>, please provide a reason.
                    </Typography>
                    <Grid container sx={{ my: 3 }}>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                required
                                label="Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                }

                <Button variant="contained"
                    onClick={() => submitCallback ? submitCallback(reason) : {}}
                    disabled={!canSubmit()}
                >
                    Resubmit
                </Button>
                <Button variant="outlined" onClick={closeCallback}>
                    Close
                </Button>

            </CardContent>
        </Card>

    );
}

const Closed = () => {
    return (<></>);
}

export const ContractVariationActions: React.FC<IContractVariationActionsFormSection> = ({
    status,
    materials,
    formValid,
    consumed,
    onEnd,
    onSubmit,
    onDelete,
    onApprove,
    onReject,
    onClose,
    onComplete,
    readonly,
    contractEnd,
    ...props
}: IContractVariationActionsFormSection) => {

    const [reason, setReason] = useState("");
    const [newContract, setNewContract] = useState(false);

    const permissionContext = useContext(PermissionContext);


    const endContractActions = EndContractActions(
        formValid ? onEnd : undefined,
        consumed,
        reason,
        setReason,
        newContract,
        setNewContract
    );

    const submitVariationActions = SubmitVariationActions(
        onDelete,
        formValid ? onSubmit : undefined,
        consumed,
        reason,
        setReason
    );

    useEffect(() => {

    }, [materials, consumed])

    return (
        <div>
            {{
                0: UnknownStatus(),
                1: contractEnd ? endContractActions : submitVariationActions,
                2: ApprovalActions(permissionContext.isRole(Role.Internal.AssetProtection), onApprove, onReject),
                3: ApprovedActions(),
                4: RejectedActions(
                    formValid ? onSubmit : undefined,
                    onClose,
                    consumed,
                    reason,
                    setReason),
                5: Closed(),
                6: Closed()
            }[status]}

            <Box display="none">
                {
                    //this is to ensure conditional rendering doesnt cause the
                    //number of hooks loaded to vary
                    contractEnd ? submitVariationActions : endContractActions
                }
            </Box>
        </div>
    );

}